import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Target, Heart, Zap, Shield, Globe, TrendingUp } from 'lucide-react';
import { Header } from './Header';
import { SEO } from './SEO';

interface AboutProps {
  onContactClick: (e: React.MouseEvent) => void;
  onAuthClick: () => void;
  onDemoClick: () => void;
}

export function About({ onContactClick, onAuthClick, onDemoClick }: AboutProps) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-zinc-900">
      <SEO 
        title="About Us - RestroCalls.AI"
        description="Learn about RestroCalls.AI's mission to revolutionize restaurant communications with AI. Discover our story, values, and commitment to helping restaurants succeed."
        path="/about"
      />
      <Header 
        onContactClick={onContactClick}
        onAuthClick={onAuthClick}
        onDemoClick={onDemoClick}
      />
      
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="mb-8">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Revolutionizing Restaurant
            <span className="text-emerald-400"> Communications</span> with AI
          </h1>
          <p className="text-xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
            We're on a mission to ensure no restaurant ever misses a customer call again
          </p>
        </div>

        {/* Our Story Section */}
        <div className="bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-800 mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-emerald-400" />
            </div>
            <h2 className="text-3xl font-bold text-white">Our Story</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-zinc-300 leading-relaxed">
              <p>
                RestroCalls.AI was founded by restaurant industry veterans who experienced firsthand the challenges of managing phone calls during peak hours. We witnessed countless missed opportunities - frustrated customers hanging up, orders lost to busy signals, and staff overwhelmed trying to balance phone service with in-person dining.
              </p>
              <p>
                We recognized that missed calls don't just mean missed revenue; they represent frustrated customers who may never return. This realization drove us to develop AI technology specifically designed for restaurant operations, not generic chatbots adapted for food service.
              </p>
              <p>
                Today, we're proud to serve hundreds of restaurants, helping them capture every opportunity while allowing their teams to focus on creating exceptional dining experiences.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=400&fit=crop" 
                alt="Restaurant team working together"
                className="rounded-xl shadow-2xl border border-zinc-700"
              />
            </div>
          </div>
        </div>

        {/* Mission Statement */}
        <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 rounded-2xl p-8 md:p-12 border border-emerald-500/20 mb-16">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
              <Target className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
            <p className="text-xl text-zinc-300 max-w-4xl mx-auto leading-relaxed">
              To empower restaurants with intelligent phone management that enhances customer experience while allowing staff to focus on what they do best - creating exceptional dining experiences.
            </p>
          </div>
        </div>

        {/* Our Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Zap className="w-6 h-6 text-emerald-400" />,
                title: "Innovation",
                description: "Cutting-edge AI technology tailored specifically for restaurant operations"
              },
              {
                icon: <Shield className="w-6 h-6 text-emerald-400" />,
                title: "Reliability",
                description: "24/7 availability with 99.9% uptime to ensure you never miss a call"
              },
              {
                icon: <Globe className="w-6 h-6 text-emerald-400" />,
                title: "Integration",
                description: "Seamless connection with existing restaurant management systems"
              },
              {
                icon: <TrendingUp className="w-6 h-6 text-emerald-400" />,
                title: "Growth",
                description: "Helping restaurants maximize revenue opportunities and scale efficiently"
              }
            ].map((value, index) => (
              <div key={index} className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800 hover:border-emerald-500/30 transition-all group">
                <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Why We're Different */}
        <div className="bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-800 mb-16">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Why We're Different</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Restaurant-Specific AI Training",
                description: "Our AI is trained specifically on restaurant scenarios, not generic customer service interactions. It understands menu items, dietary restrictions, and reservation nuances."
              },
              {
                title: "Real-Time POS Integration",
                description: "Seamlessly connects with your existing point-of-sale systems for accurate order processing and inventory management."
              },
              {
                title: "Multi-Language Support",
                description: "Serve diverse communities with AI that can communicate in multiple languages, expanding your customer base."
              },
              {
                title: "Built by Restaurant Operators",
                description: "Designed by people who understand restaurant operations from the ground up, ensuring practical solutions for real challenges."
              }
            ].map((feature, index) => (
              <div key={index} className="flex gap-4">
                <div className="w-2 h-2 rounded-full bg-emerald-400 mt-3 flex-shrink-0"></div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Team Section */}
        <div className="bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-800 mb-16">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-6">Our Team</h2>
            <p className="text-xl text-zinc-300 max-w-3xl mx-auto leading-relaxed mb-8">
              Our experienced team combines deep restaurant operations expertise with cutting-edge AI technology. We're dedicated to continuous improvement based on real restaurant feedback and evolving industry needs.
            </p>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Restaurant Veterans</h3>
                <p className="text-zinc-400 text-sm">Years of hands-on restaurant experience</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-10 h-10 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">AI Specialists</h3>
                <p className="text-zinc-400 text-sm">Cutting-edge technology expertise</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-10 h-10 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Customer-Focused</h3>
                <p className="text-zinc-400 text-sm">Dedicated to your success</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 rounded-2xl p-8 md:p-12 border border-emerald-500/20 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Join Hundreds of Successful Restaurants</h2>
          <p className="text-xl text-zinc-300 mb-8 max-w-2xl mx-auto">
            Ready to transform your restaurant's phone service? Join the growing community of restaurants that never miss a call.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button 
              onClick={onDemoClick}
              className="bg-emerald-500 text-black px-8 py-3.5 rounded-full hover:bg-emerald-400 transition shadow-lg hover:shadow-emerald-500/20"
            >
              Schedule a Demo
            </button>
            <button
              onClick={onContactClick}
              className="border-2 border-emerald-500 text-emerald-400 px-8 py-3.5 rounded-full hover:bg-emerald-500/10 transition"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-zinc-400 py-16 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <div className="relative w-8 h-8 bg-zinc-900 rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-emerald-400 rounded-full"></div>
                </div>
                <span className="font-bold text-white">RestroCalls.AI</span>
              </div>
              <p className="text-sm leading-relaxed">
                Revolutionizing restaurant phone service with intelligent AI solutions.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Solutions</h4>
              <ul className="space-y-3 text-sm">
                <li className="hover:text-emerald-400 transition cursor-pointer">Call Handling</li>
                <li className="hover:text-emerald-400 transition cursor-pointer">Reservation Management</li>
                <li className="hover:text-emerald-400 transition cursor-pointer">Order Processing</li>
                <li className="hover:text-emerald-400 transition cursor-pointer">POS Integration</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/about" className="hover:text-emerald-400 transition cursor-pointer">About Us</Link></li>
                <li className="hover:text-emerald-400 transition cursor-pointer">Case Studies</li>
                <li><Link to="/blog" className="hover:text-emerald-400 transition cursor-pointer">Blog</Link></li>
                <li><Link to="/terms" className="hover:text-emerald-400 transition cursor-pointer">Terms</Link></li>
                <li><Link to="/privacy" className="hover:text-emerald-400 transition cursor-pointer">Privacy</Link></li>
                <li>
                  <button
                    onClick={onContactClick}
                    className="hover:text-emerald-400 transition cursor-pointer"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-3 text-sm">
                <li>support@restrocalls.ai</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 mt-16 pt-8 text-sm text-center">
            © 2025 RestroCalls.AI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}